// pms-adapters / supabase / arrivals — « Arrivées », interim adapter
// (PMS-TERMINAL-LINK-BRIEF §8.8g).
//
// The Vega side of the ArrivalBriefing port: one RPC, `pms_arrivals_for_date`,
// which reads the mirror (vega_stay + vega_reservation's status letter + the
// FACT AUTO plan + the arrhes and payment ledger) and returns one row per
// arriving booking. At go-live a PMS adapter over pms_room_stay replaces it.
//
// Read-only, so no L2 gate: the RPC gates itself on `pms.payment.read`.
//
// Since §8.8k the sheet also takes the day's payments: `collected_cents` is
// what the Encaissement form took that Vega does not show yet, and
// `pms_day_payments` lists the day's form payments — what the retired Journal
// did — each with whether Vega shows it yet.
//
// `pms_booking_balances` (20261024100100) is « Suivi des paiements »: every
// booking with what it comes to, what was paid and what Vega says is owed.

import type {
  ArrivalBill,
  BookingAdvance,
  BookingBalance,
  BookingOpenNote,
  BookingPaid,
  ArrivalBriefing,
  DayPayment,
  Instant,
  IsoDate,
  ReceptionPaymentId,
  ReservationId,
  ReceptionPaymentStatus,
  Result,
  TenderType,
  PmsBoard,
  VegaPlanGap,
  VegaPlanGapKind,
} from '@lemontaigu/pms-core';
import { ok } from '@lemontaigu/pms-core';
import { fromList, type SupabaseClient } from './support.js';
import {
  toOpenSuspense,
  toPaymentLine,
  toTransferredNote,
  type PaymentRow,
  type SuspenseRow,
  type TransferredNoteRow,
} from './departures.js';

export interface ArrivalRow {
  property_code: string;
  resa: string;
  party_label: string | null;
  rooms: string[] | null;
  arrival_date: string;
  departure_date: string;
  arrived: boolean | null;
  arrival_time: string | null;
  stay_cents: number;
  deposit_cents: number;
  deposit_unattributed_cents: number;
  payments: PaymentRow[] | null;
  read_at: string | null;
  /** 20260926010100 — optional so a row of the previous shape still reads. */
  collected_cents?: number | null;
  /** 20260926030100 — likewise. */
  client_ref?: string | null;
  pending_cents?: number | null;
  suspenses?: SuspenseRow[] | null;
}

/** One row of `pms_day_payments` (20260926010100). */
export interface DayPaymentRow {
  id: number;
  property_code: string;
  resa: string | null;
  room_number: string | null;
  party_label: string | null;
  tender_type: string;
  amount_cents: number;
  status: string;
  settles_suspense_id: number | null;
  captured_at: string;
  in_vega: boolean | null;
  closed: boolean | null;
  arrives: boolean | null;
  departs: boolean | null;
}

export const toDayPayment = (r: DayPaymentRow): DayPayment => ({
  propertyCode: r.property_code,
  id: Number(r.id) as ReceptionPaymentId,
  bookingRef: r.resa,
  roomNumber: r.room_number,
  partyLabel: r.party_label,
  tenderType: r.tender_type as TenderType,
  cents: Number(r.amount_cents),
  status: r.status as ReceptionPaymentStatus,
  capturedAt: r.captured_at as Instant,
  inVega: r.in_vega === true,
  closed: r.closed === true,
  arrives: r.arrives === true,
  departs: r.departs === true,
});

/** One row of `pms_reservation_advance` (20261022220100). */
export interface BookingAdvanceRow {
  deposit_cents: number | null;
  deposit_unattributed_cents: number | null;
  collected_cents: number | null;
  payments: PaymentRow[] | null;
  read_at: string | null;
}

/** One row of `pms_reservations_paid` (20261022240100). */
export interface BookingPaidRow {
  reservation_id: string;
  paid_cents: number | string | null;
  arrhes_cents: number | string | null;
}

export const toBookingPaid = (r: BookingPaidRow): BookingPaid => ({
  reservationId: r.reservation_id as ReservationId,
  paidCents: Number(r.paid_cents ?? 0),
  arrhesCents: Number(r.arrhes_cents ?? 0),
});

export const toBookingAdvance = (r: BookingAdvanceRow): BookingAdvance => ({
  depositCents: Number(r.deposit_cents ?? 0),
  depositUnattributedCents: Number(r.deposit_unattributed_cents ?? 0),
  collectedCents: Number(r.collected_cents ?? 0),
  payments: (r.payments ?? []).map(toPaymentLine),
  readAt: r.read_at as Instant | null,
});

export const toArrivalBill = (r: ArrivalRow): ArrivalBill => ({
  propertyCode: r.property_code,
  bookingRef: r.resa,
  partyLabel: r.party_label ?? '—',
  rooms: r.rooms ?? [],
  arrivalDate: r.arrival_date as IsoDate,
  departureDate: r.departure_date as IsoDate,
  arrived: r.arrived === true,
  arrivalTime: r.arrival_time,
  stayCents: Number(r.stay_cents),
  depositCents: Number(r.deposit_cents ?? 0),
  depositUnattributedCents: Number(r.deposit_unattributed_cents ?? 0),
  payments: (r.payments ?? []).map(toPaymentLine),
  readAt: r.read_at as Instant | null,
  collectedCents: Number(r.collected_cents ?? 0),
  clientRef: r.client_ref ?? null,
  pendingCents: Number(r.pending_cents ?? 0),
  suspenses: (r.suspenses ?? []).map(toOpenSuspense),
});

/** One row of `pms_booking_balances` (20261024100100, 20261024200100) — « Suivi des paiements ». */
export interface BookingBalanceRow {
  reservation_id: string;
  property_code: string;
  resa: string | null;
  status: string;
  party_label: string | null;
  client_ref: string | null;
  external_ref: string | null;
  rooms: string[] | null;
  arrival_date: string;
  departure_date: string;
  total_cents: number | string | null;
  paid_cents: number | string | null;
  arrhes_cents: number | string | null;
  last_paid_on: string | null;
  collected_cents: number | string | null;
  pending_cents: number | string | null;
  deposit_cents: number | string | null;
  deposit_unattributed_cents: number | string | null;
  open_notes: OpenNoteRow[] | null;
  debt_cents: number | string | null;
  debts: TransferredNoteRow[] | null;
  unbilled_cents: number | string | null;
  unbilled_lines: number | null;
  read_at: string | null;
  /** Since 20261024200100. */
  invoiced_cents?: number | string | null;
}

interface OpenNoteRow {
  facture: string;
  date: string | null;
  cents: number;
  applied: number | null;
  account: string | null;
  /** Since 20261024200100. */
  guest?: string | null;
}

const toBookingOpenNote = (n: OpenNoteRow): BookingOpenNote => ({
  facture: n.facture,
  date: n.date as IsoDate | null,
  cents: Number(n.cents),
  appliedCents: Number(n.applied ?? 0),
  account: n.account,
  guest: n.guest ?? null,
});

/** A bigint column may arrive as a string; a missing one reads 0. */
const cents = (v: number | string | null | undefined): number => Number(v ?? 0);

export const toBookingBalance = (r: BookingBalanceRow): BookingBalance => ({
  reservationId: r.reservation_id as ReservationId,
  propertyCode: r.property_code,
  bookingRef: r.resa,
  status: r.status,
  partyLabel: r.party_label ?? '—',
  clientRef: r.client_ref,
  externalRef: r.external_ref,
  rooms: r.rooms ?? [],
  arrivalDate: r.arrival_date as IsoDate,
  departureDate: r.departure_date as IsoDate,
  totalCents: cents(r.total_cents),
  paidCents: cents(r.paid_cents),
  arrhesCents: cents(r.arrhes_cents),
  lastPaidOn: r.last_paid_on as IsoDate | null,
  collectedCents: cents(r.collected_cents),
  pendingCents: cents(r.pending_cents),
  depositCents: cents(r.deposit_cents),
  depositUnattributedCents: cents(r.deposit_unattributed_cents),
  openNotes: (r.open_notes ?? []).map(toBookingOpenNote),
  debtCents: cents(r.debt_cents),
  debts: (r.debts ?? []).map(toTransferredNote),
  unbilledCents: cents(r.unbilled_cents),
  unbilledLines: Number(r.unbilled_lines ?? 0),
  readAt: r.read_at as Instant | null,
  invoicedCents: cents(r.invoiced_cents),
});

/** One row of `pms_reservation_vega_plan_gap` (20261023100100). */
export interface VegaPlanGapRow {
  first_night: string;
  gap_nights: number | null;
  plan_nights: number | null;
  kind: string;
  board: string | null;
  allocation_board: string | null;
  billed: number | null;
  rooms: number | null;
  persons: number | null;
  occupancy: number | null;
  split: boolean | null;
  detail: string | null;
}

const asBoard = (b: string | null): PmsBoard | null => (b === 'hb' || b === 'bb' || b === 'none' ? b : null);

export const toVegaPlanGap = (r: VegaPlanGapRow): VegaPlanGap => ({
  firstNight: r.first_night,
  gapNights: Number(r.gap_nights ?? 0),
  planNights: Number(r.plan_nights ?? 0),
  kind: r.kind as VegaPlanGapKind,
  board: asBoard(r.board),
  allocationBoard: asBoard(r.allocation_board),
  billed: Number(r.billed ?? 0),
  rooms: Number(r.rooms ?? 0),
  persons: r.persons === null ? null : Number(r.persons),
  occupancy: r.occupancy === null ? null : Number(r.occupancy),
  split: r.split === true,
  detail: r.detail,
});

export class SupabaseVegaArrivalBriefing implements ArrivalBriefing {
  constructor(private readonly db: SupabaseClient) {}

  async arrivals(date: IsoDate): Promise<Result<ArrivalBill[]>> {
    const r = await this.db.rpc('pms_arrivals_for_date', { p_date: date });
    return fromList<ArrivalRow, ArrivalBill>(
      r as { data: ArrivalRow[] | null; error: typeof r.error },
      toArrivalBill,
    );
  }

  async dayPayments(date: IsoDate): Promise<Result<DayPayment[]>> {
    const r = await this.db.rpc('pms_day_payments', { p_date: date });
    return fromList<DayPaymentRow, DayPayment>(
      r as { data: DayPaymentRow[] | null; error: typeof r.error },
      toDayPayment,
    );
  }

  async paidTotals(reservationIds: readonly ReservationId[]): Promise<Result<BookingPaid[]>> {
    if (reservationIds.length === 0) return ok([]);
    const r = await this.db.rpc('pms_reservations_paid', { p_reservation_ids: [...reservationIds] });
    return fromList<BookingPaidRow, BookingPaid>(
      r as { data: BookingPaidRow[] | null; error: typeof r.error },
      toBookingPaid,
    );
  }

  async advance(reservationId: ReservationId): Promise<Result<BookingAdvance>> {
    const r = await this.db.rpc('pms_reservation_advance', { p_reservation_id: reservationId });
    const rows = fromList<BookingAdvanceRow, BookingAdvance>(
      r as { data: BookingAdvanceRow[] | null; error: typeof r.error },
      toBookingAdvance,
    );
    if (!rows.ok) return rows;
    // An unknown reservation answers no row: nothing paid, not an error.
    return ok(
      rows.value[0] ?? { depositCents: 0, depositUnattributedCents: 0, collectedCents: 0, payments: [], readAt: null },
    );
  }

  async bookingBalances(): Promise<Result<BookingBalance[]>> {
    const r = await this.db.rpc('pms_booking_balances', {});
    return fromList<BookingBalanceRow, BookingBalance>(
      r as { data: BookingBalanceRow[] | null; error: typeof r.error },
      toBookingBalance,
    );
  }

  async planGap(reservationId: ReservationId): Promise<Result<VegaPlanGap | null>> {
    const r = await this.db.rpc('pms_reservation_vega_plan_gap', { p_reservation_id: reservationId });
    const rows = fromList<VegaPlanGapRow, VegaPlanGap>(
      r as { data: VegaPlanGapRow[] | null; error: typeof r.error },
      toVegaPlanGap,
    );
    if (!rows.ok) return rows;
    // No row is the usual answer: the two records agree, or Vega has no plan for it.
    return ok(rows.value[0] ?? null);
  }
}
